import { useEffect, useMemo, useRef, useState } from 'react'

import type { Language, SupportedMarketKey } from '@nxt-insurance/constants'

import { getQuoteConfigPathPrefix, getStepSchemas } from '@/app/utils/flows/form'
import { useQuotePriceRefetch } from '@/hooks/useQuotePriceRefetch'
import { useZodForm } from '@/shared/components/flows/ZodForm'
import { keepPreviousData } from '@tanstack/react-query'

import type { FlowStepPageConfig, FlowStepPageProps, FlowStepQueryInput } from './types'

export const useFlowStepData = ({
  config,
  props,
  language,
  market,
  enabled,
}: {
  config: FlowStepPageConfig
  language: Language
  market: SupportedMarketKey
  props: FlowStepPageProps
  enabled: boolean
}) => {
  const query = config.router.get.useQuery(
    {
      stepName: props.stepName,
      language,
      market,
      firstStepName: props.firstStepName,
    } as FlowStepQueryInput,
    { placeholderData: keepPreviousData, enabled }
  )

  const flowConfig = useMemo(() => config.getConfig(language), [config, language])
  const schemas = useMemo(() => getStepSchemas(flowConfig.getSteps()), [flowConfig])

  const localStepConditions = useMemo(() => {
    const step = flowConfig.getSteps()[props.stepName] as
      | { stepContent?: { condition?: { type?: string } }[] }
      | undefined
    return (step?.stepContent ?? []).map((element) =>
      element?.condition?.type === 'function' ? element.condition : undefined
    )
  }, [flowConfig, props.stepName])

  const form = useZodForm({
    schema: schemas[props.stepName],
    defaultValues: query?.data?.flowValues ?? props.values,
  })

  const utils = config.useUtils()
  const priceMutation = config.router.fetchPrice.useMutation({
    onSuccess: () =>
      config.newDesign ? utils.getImmediateCoverageConsent?.invalidate() : undefined,
  })
  const { mutateAsync: mutatePrice, reset: resetPrice } = priceMutation

  useEffect(() => {
    if (priceMutation.data) {
      const { policyPreviewUrl } = priceMutation.data as { policyPreviewUrl?: string }
      form.setValue('policy_preview_url', policyPreviewUrl)
    }
  }, [form, priceMutation.data])

  const { refetchPrice, isPriceRequestPending } = useQuotePriceRefetch({
    form,
    language,
    market,
    stepName: props.stepName,
    quoteStepName: config.quoteStepName,
    quoteConfigPathPrefix: getQuoteConfigPathPrefix(config.productKey),
    livePricePaths: config.livePricePaths,
    livePriceGuards: config.livePriceGuards,
    mutatePrice,
    resetPrice,
  })

  const [syncedStepName, setSyncedStepName] = useState<string | null>(null)

  useEffect(() => {
    if (!query.data || query.isPlaceholderData) {
      return
    }
    const { flowValues, stepFlags } = query.data
    form.reset(
      stepFlags.isPaymentStep ? { ...flowValues, arePaymentDetailsValid: false } : flowValues
    )
    form.control._setValid()
    setSyncedStepName(props.stepName)
    if (stepFlags.isQuoteStep) {
      refetchPrice()
    }
  }, [form, props.stepName, query.data, query.isPlaceholderData, refetchPrice])

  const queryData = query.data
  const pgData = queryData?.productGenerationData
  const productGeneration = pgData
    ? {
        ...pgData,
        productKey: config.productKey,
        titleShort: pgData.titleShort ?? pgData.title ?? '',
        coveragePlans: pgData.coveragePlans ?? [],
        extensions: pgData.extensions ?? [],
        documents: pgData.documents ?? [],
        defaultCoveragePlan: pgData.defaultCoveragePlan ?? null,
      }
    : undefined

  const lastPriceRef = useRef(queryData?.priceData)
  if (priceMutation.data) {
    lastPriceRef.current = priceMutation.data
  } else if (queryData?.priceData && !lastPriceRef.current) {
    lastPriceRef.current = queryData.priceData
  }
  const latestPriceData = priceMutation.data ?? lastPriceRef.current ?? queryData?.priceData

  return {
    form,
    isFetchingPrice: priceMutation.isPending,
    localStepConditions,
    latestPriceData,
    productGeneration,
    query,
    refetchPrice,
    isPriceRequestPending,
    isFormSyncedForStep: syncedStepName === props.stepName,
  }
}
